import { create } from "zustand";
import axios from "axios";
import { LOG } from "../utils/logUtil";
import { useAppLoading } from "./useAppLoading";

/** The day x store counters the report sums. */
export interface IParcelReportCounts {
  registrations: number;
  customers: number;
  dropoffs: number;
  pickups: number;
  /** dropoffs + pickups. */
  parcels: number;
  /** Parcels counted on the carrier's stamp cards. */
  stamps: number;
  cardsCompleted: number;
  /** People whose first-ever Droppie drop was one of these parcels. */
  firstDrops: number;
}

export interface IParcelReportRow extends IParcelReportCounts {
  /** Amsterdam calendar day, YYYY-MM-DD. */
  date: string;
  locationId: string;
}

/** A store that appears in the rows. */
export interface IParcelReportLocation {
  locationId: string;
  name: string;
  costPlace: string;
  city: string;
}

/** An ISO week (Monday first) of the range; the first and last can be partial. */
export interface IParcelReportIndexWeek {
  /** The week's Monday, YYYY-MM-DD. */
  week: string;
  /** The first and last day of the range in this week. */
  from: string;
  to: string;
  /** Stamps at the stores that have a usual share. */
  stamps: number;
  /** Stamps at stores without a usual share, left out of the index. */
  unlistedStamps: number;
}

export interface IParcelReportIndexStore {
  locationId: string;
  name: string;
  costPlace: string;
  /** The store's usual share of the parcels, in percent. */
  share: number;
  /** That share among the stores in the index (0-1): what the index divides by. */
  expected: number;
  /** Per week, in the order of `weeks`. */
  stamps: number[];
  /** Per week: the store's share of the stamps over its usual share; null
   *  when nobody got a stamp that week. */
  index: (number | null)[];
  /** Per report day, in the order of the report's `days`. */
  dayStamps: number[];
}

export interface IParcelReportStoreIndex {
  weeks: IParcelReportIndexWeek[];
  /** Per report day: stamps at the stores that have a usual share, and at
   *  stores without one (left out of the index). */
  dayStamps: number[];
  dayUnlistedStamps: number[];
  stores: IParcelReportIndexStore[];
}

export interface IParcelReport {
  /** _id "all" with name "All carriers" when every carrier is asked for. */
  carrier: { _id: string; name: string };
  timezone: string;
  dateFrom: string;
  dateTo: string;
  generatedAt: string;
  /** Every Amsterdam day in the range, ascending, YYYY-MM-DD. */
  days: string[];
  stores: IParcelReportLocation[];
  /** Sparse: only the day x store pairs that had activity. */
  rows: IParcelReportRow[];
  /** customers is distinct over the whole range, not a sum of the rows. */
  totals: IParcelReportCounts;
  excluded: { selfTests: number };
  /** For every carrier and for all carriers, never for a store admin. Covers
   *  every store whatever store is picked. */
  storeIndex?: IParcelReportStoreIndex | null;
}

export interface IParcelReportParams {
  /** A carrier partner's id, or ALL_CARRIERS for every carrier. */
  carrierPartnerId: string;
  /** Inclusive Amsterdam days, YYYY-MM-DD. */
  dateFrom: string;
  dateTo: string;
  /** Left out for all stores. Ignored by the backend for a store admin. */
  locationIds?: string[];
}

interface IStore {
  /** State */
  parcelReport: IParcelReport | null;
  isLoading: boolean;
  error: string | null;
  /** Callbacks */
  getParcelReport: (params: IParcelReportParams) => void;
  clearParcelReport: () => void;
}

/** Incremented per request; only the newest is allowed to write to state. */
let requestSequence = 0;

/**
 * Requests still in flight, by query string. The shared axios setup refuses a
 * request identical to one already in flight and resolves it with no
 * response, so a repeat (StrictMode's double mount in development, or a filter
 * changed and changed back faster than the server answers) would be the
 * newest request and have nothing to show. Sharing the pending request gives
 * the repeat the same answer.
 */
const inFlight = new Map<string, Promise<any>>();

const fetchReport = (query: string) => {
  let pending = inFlight.get(query);
  if (!pending) {
    pending = axios
      .get(`parcel-handoffs/stats/daily?${query}`)
      .finally(() => inFlight.delete(query));
    inFlight.set(query, pending);
  }
  return pending;
};

export const useParcelReport = create<IStore>()((set) => ({
  /** State */
  parcelReport: null,
  isLoading: false,
  error: null,
  /** Callbacks */
  getParcelReport: async (params) => {
    // Monotonic sequence, checked before anything is written to state. Filter
    // changes fire requests faster than they come back, and without this an
    // earlier, slower response lands last and replaces the report for the
    // filters the operator is actually looking at.
    const sequence = ++requestSequence;
    const isStale = () => sequence !== requestSequence;

    useAppLoading.getState().setIsLoading(true);
    // The previous report stays in place while this one loads; the screen
    // dims it instead of flashing empty charts.
    set({ isLoading: true, error: null });
    try {
      const query = new URLSearchParams({
        carrierPartnerId: params.carrierPartnerId,
        dateFrom: params.dateFrom,
        dateTo: params.dateTo,
      });
      if (params.locationIds?.length) {
        query.append("locationIds", params.locationIds.join(","));
      }

      const response = await fetchReport(query.toString());
      if (isStale()) return;
      if (!response?.data) throw new Error("Empty parcel report response");

      set({ parcelReport: response.data });
    } catch (error: any) {
      if (isStale()) return;
      LOG.error("Error on fetching the parcel report", { error });
      set({
        parcelReport: null,
        // The shared interceptor rejects with the response itself, so the
        // body sits at error.data; the nested shape is kept as a fallback.
        error:
          error?.data?.message ??
          error?.response?.data?.message ??
          "Could not load the parcel report. Please try again.",
      });
    } finally {
      if (!isStale()) {
        set({ isLoading: false });
      }
      useAppLoading.getState().setIsLoading(false);
    }
  },
  clearParcelReport: () => {
    // Bumping the sequence cancels any in-flight response, so a request from
    // the screen we just left cannot repopulate the report behind us.
    requestSequence += 1;
    set({ parcelReport: null, isLoading: false, error: null });
  },
}));
